import type { QuoteFile } from 'paintscout'

/**
 * Character limits for a media item's editable text fields. These cap typed
 * input in the editor (via `maxLength`) and are also the thresholds used to
 * block a save when text arrives programmatically (e.g. a CompanyCam photo
 * description), where `maxLength` doesn't apply.
 */
export const FILE_TITLE_MAX_LENGTH = 100
export const FILE_CAPTION_MAX_LENGTH = 250

export interface FileTextLimits {
  titleMax?: number
  captionMax?: number
}

/**
 * True when a file's `title` or `caption` is *over* the given limit. Only
 * genuine overflow counts (`>`), so content sitting exactly at the limit is
 * fine. Used to turn fields red and to block saving until the user shortens the
 * text themselves — rather than silently truncating and dropping potentially
 * important information.
 */
export function isFileTextOverLimit(
  file: QuoteFile,
  { titleMax = FILE_TITLE_MAX_LENGTH, captionMax = FILE_CAPTION_MAX_LENGTH }: FileTextLimits = {}
): boolean {
  const titleTooLong = !!file.title && file.title.length > titleMax
  const captionTooLong = !!file.caption && file.caption.length > captionMax
  return titleTooLong || captionTooLong
}

/**
 * Returns the subset of `files` whose title/caption exceeds the limits, in the
 * original order. The first entry is the one to navigate the user to so they can
 * shorten it. Returns an empty array when everything is within the limits.
 */
export function findFilesOverTextLimit<T extends QuoteFile>(files: T[], limits: FileTextLimits = {}): T[] {
  return files.filter((file) => isFileTextOverLimit(file, limits))
}
